import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, Calendar, Check, Clock, FileText, LogOut, Plane, Plus, Eye,
  RotateCcw, Wallet, type LucideIcon,
} from '../../icons';
import {
  Button, Checkbox, FormField, Input, Modal, ModalActions, ModalFootNote, Select, Tabs,
} from '../../components/shared';
// 손짠 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { PageHeader } from '../../components/shared';
// 저장 줄 한 벌(2026-10-18 · web 레인 W8b)
import { SaveBar } from '../../components/shared';
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import {
  listDocTypes, createDocType, updateDocType, resetDocType,
  type DocTypeDto, type DocTypeCategory, type FinalizeKind,
} from '../../api/approval-doc-type';
// 칸 편집 — 팝업이 아니라 「칸」 탭 안에서 산다 (2026-08-31 · 시안 7/9).
// 결재함 (2026-09-07) — 양식의 묶음이자 전사결재함. 못 읽으면 그 칸이 안 선다.
import { listApprovalBoxes, type ApprovalBoxDto } from '../../api/approval-box';
import { FormSchemaEditor } from './FormSchemaEditor';
// 미리 보기 — 기안자가 볼 화면을 **그대로** 그린다(같은 컴포넌트라 어긋날 수 없다).
import { ApprovalFormFields, type FormValues } from './ApprovalFormFields';
import type { ApprovalFormField } from '../../types/approval';
// 숫자 구분 기호는 **언어 설정**을 따른다 — 하드코딩 로케일은 `lib/i18n-locale-hardcode.test.ts` 가 막는다.
import { getNumberLocale } from '../../lib/base-i18n';
import { getInstitution } from '../../api/institution';
import '../AttendancePage/AttendanceAdmin.css';
import './DocTypePage.css';

/**
 * 관리 — **양식 · 서식** (2026-08-31 · 시안 맞춤 7/9).
 *
 * 시안: `docs/hr-attendance-approval-redesign-sample.html` 「F2 양식 · 서식 — 배포 없이 양식이 는다」.
 *
 * ★★★ **한 장짜리 넓은 표였다.** 종류 · 코드 · 구분 · 기본 단계 · 기안자 수정 · 전결 기준 ·
 *    서식 · 사용 — 여덟 열이 한 줄에 서고, 칸(서식)은 **팝업**으로만 열렸다. 그래서
 *    ① 한 양식을 손보는 동안 그 양식의 다른 설정을 못 보고,
 *    ② 양식이 늘수록 가로로 흘러 「이 회사가 무슨 양식을 쓰나」가 안 보였다.
 *    시안이 **좌 목록 + 우 상세(탭 넷)** 로 그린 이유가 그것이다.
 *
 * ★ **지우는 버튼이 없다.** 이미 그 종류로 올라간 문서가 있어서, 종류가 사라지면 그 문서들이
 *   무엇인지 알 수 없게 된다 — 대신 끄면 **목록에서만** 빠진다(이미 올라간 문서는 그대로 돈다).
 * ★ **코드는 만든 뒤에 못 바꾼다.** `approval_documents.type` 이 그 값을 들고 있다. 화면에서도
 *   잠그고, 서버도 400 으로 막는다.
 * ★ 단계 수는 **기본값이지 상한이 아니다** — 조직 사다리가 더 길면 사다리를 따른다.
 *
 * ★★★ **전결은 여태 저장이 안 됐다** (2026-08-31 에 함께 고쳤다). 칸은 migration 124 때부터
 *    있었는데 서버 PATCH 스키마에 그 셋이 없어, fastify 의 ajv 가 **조용히 지우고** 200 을
 *    줬다 — 화면은 「저장했습니다」라고 말하고 새로고침하면 옛 값이 돌아왔다.
 */

const ERRORS: Record<string, string> = {
  FORBIDDEN: '이 화면은 회사 관리자만 쓸 수 있습니다.',
  DUPLICATE_CODE: '이미 있는 코드입니다.',
  CODE_IMMUTABLE: '코드는 바꿀 수 없습니다 — 새 종류를 만드세요.',
  NOT_FOUND: '그 문서 종류를 찾을 수 없습니다.',
};

/**
 * 좌측 묶음 — **구분(`category`) 그대로다.**
 *
 * ⚠ 시안 그림은 「근태 · 휴가 / 회계 / 일반」 셋으로 나눠 그렸지만, 같은 시안의 머리는
 *   「근태 5 · 그 외 3」이라고 센다. 서버가 아는 구분은 **둘**(`HR` | `GENERAL`)이고,
 *   셋째 묶음을 만들려면 그 값을 읽는 코드가 함께 있어야 뜻이 생긴다 — 그림의 세 줄은
 *   예시이지 결정이 아니므로 **여기서 값을 늘리지 않는다**.
 */
/**
 * 공통 / 우리 회사 — **파생으로 가른다** (2026-09-01 · 판 13 ⑤).
 *
 * ★ `origin` 이 없으면(옛 서버) 「공통」으로 본다 — 빈 묶음은 아예 안 그리므로,
 *   그때 화면은 종전처럼 **한 묶음**으로 보인다.
 */
const ORIGINS: Array<{ key: 'COMMON' | 'COMPANY'; label: string; note: string }> = [
  { key: 'COMMON', label: '공통 양식', note: '모든 회사가 씁니다' },
  { key: 'COMPANY', label: '우리 회사 양식', note: '우리만 씁니다' },
];

const GROUPS: Array<{ key: DocTypeCategory; label: string }> = [
  { key: 'HR', label: '근태 · 휴가' },
  { key: 'GENERAL', label: '그 외' },
];

/**
 * 양식마다 **제 그림** (2026-08-31 · 시안 F2 — 사용자 지적 「양식 · 서식 화면도 다르다」).
 *
 * ★ 시안 좌측 목록은 항목마다 아이콘을 이고 있다(달력 · 시계 · 비행기 · 되돌리기 · 지갑…).
 *   이름만 여덟 줄이면 어느 것이 무엇인지 **글자를 읽어야** 알고, 회사가 양식을 늘릴수록 더 그렇다.
 * ★ **코드로 고른다** — 이름은 회사가 바꿀 수 있지만 코드는 못 바꾼다(`approval_documents.type`).
 * ★ 모르는 코드(회사가 새로 연 양식)는 **종이**다 — 그림이 없다고 목록이 깨지면 안 된다.
 */
const ICON_BY_CODE: Record<string, LucideIcon> = {
  LEAVE: Calendar,
  OVERTIME: Clock,
  ATTENDANCE_FIX: RotateCcw,
  OUTSIDE_WORK: Plane,
  BUSINESS_TRIP: Plane,
  EARLY_LEAVE: LogOut,
  EXPENSE: Wallet,
  OTHER: FileText,
  // ★ 업무 보고 셋 (2026-09-01) — 그림이 없어도 종이로 뜨지만, 여덟과 나란히 서는 자리라 붙인다.
  REPORT_DAILY: FileText,
  REPORT_WEEKLY: FileText,
  REPORT_MONTHLY: FileText,
};

/** 전결을 무엇으로 재나 (결재 A3) — 안 함 / 일수 / 금액. */
const FINALIZE_OPTIONS: Array<{ value: FinalizeKind; label: string; unit: string }> = [
  { value: 'NONE', label: '안 함', unit: '' },
  { value: 'DAYS', label: '일수', unit: '일' },
  { value: 'AMOUNT', label: '금액', unit: '원' },
];

type TabId = 'basic' | 'fields' | 'line' | 'finalize';

/** ★ 「500000원」은 못 읽는다 — 자릿수를 끊어 준다(일수 0.25 도 그대로 나온다). */
const num = (v: number) => v.toLocaleString(getNumberLocale());

const EMPTY_DRAFT = { code: '', name: '', defaultSteps: 1, category: 'GENERAL' as DocTypeCategory };

/**
 * 지어질 코드를 **미리 보여 준다** (2026-09-01 · 판 13 ④).
 *
 * ★ 서버가 짓는 규칙과 같은 셈을 화면에서도 한다 — 「무엇이 될지」를 만들기 전에 알려 주려는 것이다.
 *   실제 값은 **서버가 정한다**(그 사이 다른 담당자가 하나 더 만들었을 수 있다).
 */
function previewCode(institutionCode: string | null, taken: readonly string[]): string {
  const cleaned = (institutionCode ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const head = cleaned === '' ? 'C' : cleaned.slice(0, 12);
  const prefix = /^[A-Z]/.test(head) ? head : `C${head}`.slice(0, 12);
  const re = new RegExp(`^${prefix}-(\\d+)$`);
  let max = 0;
  for (const c of taken) {
    const m = re.exec(c);
    if (m) { const n = Number(m[1]); if (Number.isSafeInteger(n) && n > max) max = n; }
  }
  return `${prefix}-${String(max + 1).padStart(4, '0')}`;
}

export function DocTypePage(): JSX.Element {
  const [items, setItems] = useState<DocTypeDto[]>([]);
  const [selId, setSelId] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>('basic');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  /** 「양식 만들기」 팝업. 화면 맨 아래 폼이던 것이 머리 단추로 올라왔다. */
  const [draft, setDraft] = useState<typeof EMPTY_DRAFT | null>(null);
  /** 회사코드 — 지어질 코드를 미리 보여 주는 데만 쓴다. 못 읽으면 안 보여 준다. */
  const [instCode, setInstCode] = useState<string | null>(null);
  /** 회사의 결재함 — 「기본」 탭의 묶음 칸이 쓴다. 못 읽으면 빈 배열이라 그 칸이 안 선다. */
  const [boxes, setBoxes] = useState<ApprovalBoxDto[]>([]);
  /** 미리 보기 팝업 — 기안자가 볼 화면 그대로. */
  const [preview, setPreview] = useState<DocTypeDto | null>(null);
  // 공통 양식으로 초기화 — 묻는 창에 띄운 양식(2026-10-18 · web 레인 W8c)
  const [resetting, setResetting] = useState<DocTypeDto | null>(null);
  /** 「기본」 탭의 편집 값 — 고른 양식이 바뀌면 다시 채운다. */
  const [basic, setBasic] = useState<{ name: string; category: DocTypeCategory }>(
    { name: '', category: 'GENERAL' },
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await listDocTypes(true);
      setItems(next);
      return next;
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
      setError(ERRORS[code] ?? '문서 종류를 불러오지 못했습니다.');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  // ★ 회사코드는 «지어질 코드» 를 보여 주는 데만 쓴다 — 못 읽으면 조용히 안 보여 준다.
  useEffect(() => {
    getInstitution().then((i) => setInstCode(i.code ?? null)).catch(() => setInstCode(null));
  }, []);
  // 결재함 — 옛 서버에는 이 라우트가 없다(404). 못 읽으면 「묶음」 칸을 아예 안 그린다.
  useEffect(() => {
    listApprovalBoxes().then(setBoxes).catch(() => setBoxes([]));
  }, []);

  /**
   * 고른 양식 — 아직 안 골랐으면 **첫 번째**다(빈 오른쪽 칸으로 시작하면 무엇을 눌러야 할지 모른다).
   *
   * ★ 이것을 `useEffect` + `setSelId` 로 하지 않는 것은 일부러다 — 「아직 null 이면 첫 번째로」
   *   효과는 **사람이 먼저 누른 뒤에 뒤늦게 돌아** 그 선택을 덮을 수 있다(검사에서 실제로 걸렸다).
   *   파생 값으로 두면 그런 순간이 아예 없다.
   */
  const sel = useMemo(
    () => items.find((x) => x.id === selId) ?? items[0] ?? null,
    [items, selId],
  );

  useEffect(() => {
    if (!sel) return;
    setBasic({ name: sel.name, category: (sel.category as DocTypeCategory) ?? 'GENERAL' });
  }, [sel]);

  const fail = (err: unknown, fallback: string) => {
    const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
    setError(ERRORS[code] ?? fallback);
  };

  const onCreate = async () => {
    if (!draft) return;
    setBusy(true); setError(null); setDone(null);
    try {
      /**
       * ★★★ **코드를 안 적었으면 «안 싣는다»** — 그러면 서버가 짓는다 (판 13 ④).
       *    빈 문자열을 보내면 옛 서버가 400(패턴 불일치)을 내고, 새 서버도 «준 것»으로 본다.
       */
      const typed = draft.code.trim().toUpperCase();
      const made = await createDocType({
        name: draft.name.trim(),
        defaultSteps: draft.defaultSteps,
        category: draft.category,
        ...(typed !== '' ? { code: typed } : {}),
      });
      setDraft(null);
      setDone('새 양식을 만들었습니다. 「칸」 탭에서 서식을 갖추세요.');
      await load();
      setSelId(made.id);
      setTab('fields');
    } catch (err) {
      fail(err, '만들지 못했습니다.');
    } finally { setBusy(false); }
  };

  /**
   * 기본으로 되돌리기 — 얹은 행을 지운다 (판 13 ⑤).
   *
   * ★ 지운 뒤 고른 것을 **코드로 다시 잡는다** — 행이 사라지면 id 가 `common:LEAVE` 로 바뀌어
   *   옛 id 로는 아무것도 못 찾는다(오른쪽이 빈 채로 남는다).
   */
  const onReset = async (id: string) => {
    const code = items.find((t) => t.id === id)?.code ?? null;
    setBusy(true); setError(null); setDone(null);
    try {
      await resetDocType(id);
      setDone('공통 양식의 기본값으로 되돌렸습니다.');
      const next = await load();
      setSelId(next?.find((t) => t.code === code)?.id ?? null);
    } catch (err) {
      fail(err, '되돌리지 못했습니다.');
    } finally { setBusy(false); }
  };

  const onPatch = async (id: string, patch: Parameters<typeof updateDocType>[1], note: string) => {
    setBusy(true); setError(null); setDone(null);
    try {
      await updateDocType(id, patch);
      setDone(note);
      await load();
    } catch (err) {
      fail(err, '저장하지 못했습니다.');
    } finally { setBusy(false); }
  };

  const hr = items.filter((i) => i.category === 'HR').length;
  const general = items.length - hr;
  const draftCode = (draft?.code ?? '').trim().toUpperCase();
  /**
   * ★★★ **이름만 있으면 만들 수 있다** (2026-09-01 · 판 13 ④) — 코드는 서버가 짓는다.
   *    종전에는 코드가 형식에 맞아야만 「만들기」가 열렸다. 영어를 모르면 못 만들었다.
   * ★ 직접 적었으면 **그때만** 형식을 본다 — 잘못 적은 채로 보내면 서버가 400 이다.
   *   붙임표를 받는다(채번이 쓰는 글자라 손으로도 쓸 수 있어야 앞뒤가 맞는다).
   */
  const codeOk = draftCode === '' || (/^[A-Z][A-Z0-9_-]*$/.test(draftCode) && draftCode.length >= 2);
  const canCreate = codeOk && (draft?.name ?? '').trim() !== '';

  const fieldCount = (t: DocTypeDto) => t.formSchema?.fields.length ?? 0;

  return (
    <div className="hra dtp">
      {/* ★ 제목은 **메뉴 이름과 같아야 한다** — 트리에 적힌 이름을 보고 누르기 때문이다
          (`lib/hr-menu-titles.test.ts` 가 둘이 갈리는 것을 막는다). */}
      <PageHeader
        breadcrumb={crumbsFor('/approval/admin/forms')}
        scope={scopeFor('/approval/admin/forms')}
        title="양식 · 서식"
        description={<>회사가 연 양식 {items.length}종 — 근태 {hr} · 그 외 {general}</>}
        actions={(
          <Button onClick={() => setDraft(EMPTY_DRAFT)} iconLeft={<Plus size={14} aria-hidden />}>
            양식 만들기
          </Button>
        )}
      />

      {error && <p className="hra__error" role="alert"><AlertCircle size={14} aria-hidden />{error}</p>}
      {done && <p className="hra__ok"><Check size={14} aria-hidden />{done}</p>}
      {loading && items.length === 0 && <p className="hra__empty">불러오는 중…</p>}
      {!loading && items.length === 0 && !error && (
        <p className="hra__empty">아직 양식이 없습니다 — 「양식 만들기」로 첫 양식을 여세요.</p>
      )}

      {items.length > 0 && (
        <div className="dtp__body">
          {/* ── 좌: 회사가 연 양식 ── */}
          <nav className="dtp__list" aria-label="양식 목록">
            {/*
              ★★★ **공통 양식과 «우리 회사 양식»을 가른다** (2026-09-01 · 판 13 ⑤).
                 공통 열한 개는 **모든 회사의 바닥**이라 표에 행이 없어도 뜬다. 회사가 고치면
                 그때 행이 생기고(얹기) 「회사가 고침」 딱지가 붙는다 — 그때만 되돌릴 것이 있다.
              ★ 옛 서버는 `origin` 을 안 준다 → **전부 「공통」으로 묶인다**. 그 서버가 돌던
                시절에는 회사가 만든 양식이 곧 표의 전부였지만, 그때도 다 «회사가 쓰는 것»이라
                한 묶음으로 보이는 것이 틀린 그림은 아니다(딱지도 안 붙는다).
            */}
            {ORIGINS.map((o) => {
            const inOrigin = items.filter((t) => (t.origin ?? 'COMMON') === o.key);
            if (inOrigin.length === 0) return null;
            return (
            <div className="dtp__origin" key={o.key}>
              <div className="dtp__olabel">
                {o.label}<span>{inOrigin.length} · {o.note}</span>
              </div>
            {GROUPS.map((g) => {
              const rows = inOrigin.filter((t) => (t.category ?? 'GENERAL') === g.key);
              if (rows.length === 0) return null;
              return (
                <div className="dtp__grp" key={g.key}>
                  <div className="dtp__glabel">{g.label}</div>
                  {rows.map((t) => {
                    const Icon = ICON_BY_CODE[t.code] ?? FileText;
                    return (
                    <button
                      key={t.id}
                      type="button"
                      className={`dtp__item${t.id === sel?.id ? ' is-on' : ''}${t.isActive ? '' : ' is-off'}`}
                      aria-current={t.id === sel?.id ? 'true' : undefined}
                      onClick={() => setSelId(t.id)}
                    >
                      <Icon size={15} className="dtp__ico" aria-hidden />
                      <span className="dtp__nm">{t.name}</span>
                      {/* ★ 「칸 N」이 목록에 서야 **서식을 안 갖춘 양식**이 눈에 걸린다. */}
                      <span className="dtp__cnt">{fieldCount(t) > 0 ? `칸 ${fieldCount(t)}` : '칸 0'}</span>
                      {/* ★ 고친 적이 있을 때만 딱지가 붙는다 — 그때만 되돌릴 것이 있다. */}
                      {t.overridden && <span className="dtp__ovtag">회사가 고침</span>}
                      {!t.isActive && <span className="dtp__offtag">꺼짐</span>}
                    </button>
                    );
                  })}
                </div>
              );
            })}
            </div>
            );
            })}
          </nav>

          {/* ── 우: 고른 양식 ── */}
          <section className="dtp__pane" aria-label="양식 상세">
            {!sel ? (
              <p className="hra__empty">왼쪽에서 양식을 고르세요.</p>
            ) : (
              <>
                <div className="dtp__dh">
                  <h2 className="dtp__dt">{sel.name}</h2>
                  <span className="dtp__codeline">코드 <code className="dtp__code">{sel.code}</code> · 바꿀 수 없음</span>
                  <span className={`dtp__use${sel.isActive ? '' : ' is-off'}`}>
                    {sel.isActive ? '사용 중' : '사용 안 함'}
                  </span>
                  <span className="dtp__sp" />
                  {/*
                    ★★★ **되돌리기 = 얹은 행을 지우는 것** (2026-09-01 · 판 13 ⑤).
                       바닥(공통 카탈로그)은 건드린 적이 없으므로 「원래 값이 뭐였더라」를
                       아무도 기억할 필요가 없다. 고친 적이 없으면 되돌릴 것도 없다.
                    ★ 회사가 만든 양식에는 안 뜬다 — 그건 지우는 게 아니라 **끄는** 것이다
                      (그 코드로 이미 올라간 문서가 이름을 잃는다).
                  */}
                  {sel.overridden && (
                    <Button
                      variant="secondary" size="compact" disabled={busy}
                      onClick={() => setResetting(sel)}
                      iconLeft={<RotateCcw size={14} aria-hidden />}
                    >
                      {/* W8c(평 31-1) — 아래 저장바 «되돌리기»(저장 전 고친 칸)와 이름 · 뜻을 가르고, 바로 지우지 않고 묻는다. */}
                      공통 양식으로 초기화…
                    </Button>
                  )}
                  <Button
                    variant="secondary" size="compact" onClick={() => setPreview(sel)}
                    iconLeft={<Eye size={14} aria-hidden />}
                  >
                    미리 보기
                  </Button>
                </div>

                <Tabs
                  className="dtp__tabs"
                  ariaLabel="양식 설정"
                  value={tab}
                  onChange={(v) => setTab(v as typeof tab)}
                  items={[
                    { value: 'basic', label: '기본' },
                    { value: 'fields', label: `칸 ${fieldCount(sel)}` },
                    { value: 'line', label: '결재선' },
                    { value: 'finalize', label: '전결' },
                  ]}
                />

                <div className="dtp__tabbody" role="tabpanel">
                  {tab === 'basic' && (
                    <BasicTab
                      docType={sel} basic={basic} setBasic={setBasic} busy={busy}
                      onPatch={onPatch} boxes={boxes}
                    />
                  )}

                  {tab === 'fields' && (
                    <FormSchemaEditor
                      key={sel.id}
                      docType={sel}
                      onSaved={(next) => {
                        setItems((prev) => prev.map((x) => (x.id === next.id ? next : x)));
                        setDone(`${next.name}의 서식을 저장했습니다.`);
                      }}
                    />
                  )}

                  {tab === 'line' && <LineTab docType={sel} busy={busy} onPatch={onPatch} />}
                  {tab === 'finalize' && <FinalizeTab docType={sel} busy={busy} onPatch={onPatch} />}
                </div>
              </>
            )}
          </section>
        </div>
      )}

      {/* ── 양식 만들기 ── */}
      {draft && (
        <Modal title="양식 만들기" size="md" onClose={() => setDraft(null)} footer={
          <>
            <ModalFootNote>
              코드는 <b>만든 뒤에 못 바꿉니다</b> — 이미 그 종류로 올라간 문서가 코드를 들고 있습니다.
            </ModalFootNote>
            <ModalActions>
              <Button variant="secondary" onClick={() => setDraft(null)}>취소</Button>
              <Button
                disabled={!canCreate || busy}
                onClick={() => { void onCreate(); }}
              >{busy ? '만드는 중…' : '만들기'}</Button>
            </ModalActions>
          </>
        }>
          <div className="dtp__mkform">
            <FormField className="hra__field" label="이름">
              <Input
                fullWidth
                type="text" value={draft.name} placeholder="예: 경조사 신청" maxLength={100} autoFocus
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </FormField>
            {/*
              ★★★ **코드 칸을 없앴다** (2026-09-01 · 판 13 ④).
                 이 값은 **만든 뒤 못 바꾼다**(`approval_documents.type` 에 박힌다). 가장 바꾸기
                 어려운 값을 가장 준비 안 된 순간에 짓게 하고 있었고, 겹치면 저장을 누른 **뒤에**
                 409 로 튕겼다. 이제 서버가 짓는다 — 중복이 구조적으로 없다.
              ★ 접어 둔 자리는 **다른 시스템과 맞춰야 할 때**만 편다.
            */}
            <details className="dtp__mkcode">
              <summary>코드 직접 정하기</summary>
              <Input
                fullWidth
                type="text" value={draft.code} placeholder="CONDOLENCE" maxLength={50}
                aria-label="코드"
                onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
              />
              <p className="hra__note">
                대문자·숫자·밑줄·붙임표만 씁니다. 비워 두면 서버가 짓습니다.
              </p>
            </details>
            <FormField className="hra__field" label="구분">
              <Select
                value={draft.category}
                ariaLabel="구분"
                options={[{ value: 'HR', label: '근태' }, { value: 'GENERAL', label: '그 외' }]}
                onChange={(v) => setDraft({ ...draft, category: v as DocTypeCategory })}
              />
            </FormField>
            <FormField className="hra__field" label="기본 단계">
              <Input
                fullWidth
                type="number" min={1} max={10} value={draft.defaultSteps}
                onChange={(e) => setDraft({ ...draft, defaultSteps: Number(e.target.value) })}
              />
            </FormField>
          </div>
          <p className="hra__note">
            {draft.code.trim() === '' && instCode !== null && (
              <>코드는 <b>{previewCode(instCode, items.map((t) => t.code))}</b> 로 지어집니다 —
              만든 뒤에는 바꿀 수 없습니다.<br /></>
            )}
            만들고 나면 <b>「칸」 탭</b>에서 서식을 갖추세요 — 칸이 없으면 제목과 본문만 받습니다.
          </p>
        </Modal>
      )}

      {preview && <PreviewModal docType={preview} onClose={() => setPreview(null)} />}

      {/* 공통 양식으로 초기화 — 묻는 창(2026-10-18 · web 레인 W8c · 평 31-1).
          ★ 하는 일은 종전 onReset 그대로(얹은 행을 지운다). «무엇이 바뀌나» 목록은 짝 문서 0-16 이 오면 넣는다. */}
      {resetting && (
        <Modal title="공통 양식으로 초기화" size="sm" onClose={() => setResetting(null)} footer={
          <ModalActions>
            <Button variant="secondary" onClick={() => setResetting(null)}>취소</Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => { const id = resetting.id; setResetting(null); void onReset(id); }}
            >초기화</Button>
          </ModalActions>
        }>
          <p>«{resetting.name}»에서 이 회사가 고친 값이 모두 공통 양식의 값으로 돌아갑니다.</p>
          <p className="hra__dim">되돌릴 수 없습니다 — 다시 바꾸려면 처음부터 고쳐야 합니다.</p>
        </Modal>
      )}
    </div>
  );
}

/* ═══════════════ 기본 ═══════════════ */

function BasicTab({ docType, basic, setBasic, busy, onPatch, boxes }: {
  docType: DocTypeDto;
  basic: { name: string; category: DocTypeCategory };
  setBasic: (v: { name: string; category: DocTypeCategory }) => void;
  busy: boolean;
  onPatch: (id: string, patch: Parameters<typeof updateDocType>[1], note: string) => Promise<void>;
  /** 회사의 결재함 — 못 읽었으면 빈 배열이고, 그때 「묶음」 칸이 안 선다. */
  boxes: ApprovalBoxDto[];
}) {
  const dirty = basic.name.trim() !== docType.name
    || basic.category !== ((docType.category as DocTypeCategory) ?? 'GENERAL');

  return (
    <div className="dtp__form">
      <div className="dtp__row">
        <FormField className="hra__field" label="이름">
          <Input
            fullWidth
            type="text" value={basic.name} maxLength={100} disabled={busy}
            onChange={(e) => setBasic({ ...basic, name: e.target.value })}
          />
        </FormField>
        <FormField className="hra__field" label="구분">
          <Select
            value={basic.category} disabled={busy}
            ariaLabel="구분"
            options={[{ value: 'HR', label: '근태' }, { value: 'GENERAL', label: '그 외' }]}
            onChange={(v) => setBasic({ ...basic, category: v as DocTypeCategory })}
          />
        </FormField>
        {/* ★ 코드는 **보여 주되 잠근다** — 감추면 「왜 못 고치나」를 물을 자리가 없어진다. */}
        <FormField className="hra__field" label="코드">
          <Input fullWidth type="text" value={docType.code} readOnly aria-readonly="true" />
        </FormField>
      </div>

      {/*
        ── 회사 관리자가 정하는 셋 (2026-09-07 · 시안 A1) ──────────────────────
        ★★★ 이 셋이 「결재양식」 창의 오른쪽을 채운다. 기안자는 **제목 하나만** 치고,
           그 제목마저 여기서 정한 값(없으면 양식 이름)으로 미리 차 있다.
        ★★ **결재함이 곧 그 창의 왼쪽 묶음**이다 — 기안자가 고르는 칸이 아니다.
      */}
      <div className="dtp__row">
        {boxes.length > 0 && (
          <FormField className="hra__field" label="결재함">
            <Select
              value={docType.boxCode ?? ''}
              disabled={busy}
              ariaLabel={`${docType.name} 결재함`}
              options={[
                { value: '', label: '묶음 없음' },
                ...boxes.filter((b) => b.isActive).map((b) => ({ value: b.code, label: b.name })),
              ]}
              onChange={(v) => void onPatch(
                docType.id,
                { boxCode: v === '' ? null : v },
                v === ''
                  ? `${docType.name}을(를) 묶음에서 뺐습니다.`
                  : `${docType.name}을(를) ${boxes.find((b) => b.code === v)?.name ?? v} 결재함에 넣었습니다.`,
              )}
            />
          </FormField>
        )}
        <FormField className="hra__field" label="보존연한" hint="비우면 5년">
          <Input
            className="hra__num"
            type="number" min={1} max={99}
            aria-label={`${docType.name} 보존연한`}
            value={docType.retentionYears ?? ''} disabled={busy}
            placeholder="5"
            onChange={(e) => {
              const raw = e.target.value.trim();
              const v = raw === '' ? null : Number(raw);
              if (v !== null && (!Number.isInteger(v) || v < 1 || v > 99)) return;
              if (v === (docType.retentionYears ?? null)) return;
              void onPatch(
                docType.id, { retentionYears: v },
                v === null
                  ? `${docType.name}의 보존연한을 기본(5년)으로 되돌렸습니다.`
                  : `${docType.name}의 보존연한을 ${v}년으로 바꿨습니다.`,
              );
            }}
          />
        </FormField>
        <FormField className="hra__field" label="기본 제목" hint="비우면 양식 이름">
          <Input
            fullWidth
            type="text" maxLength={200} disabled={busy}
            aria-label={`${docType.name} 기본 제목`}
            defaultValue={docType.defaultTitle ?? ''}
            placeholder={docType.name}
            onBlur={(e) => {
              const v = e.target.value.trim();
              const next = v === '' ? null : v;
              if (next === (docType.defaultTitle ?? null)) return;
              void onPatch(
                docType.id, { defaultTitle: next },
                next === null
                  ? `${docType.name}의 기본 제목을 양식 이름으로 되돌렸습니다.`
                  : `${docType.name}의 기본 제목을 「${next}」로 바꿨습니다.`,
              );
            }}
          />
        </FormField>
      </div>
      <p className="hra__note">
        <b>결재함</b>은 기안 창의 <b>왼쪽 묶음</b>이자 이 양식으로 올린 문서가 들어갈
        <b> 전사결재함</b>입니다 — 기안자는 고르지 않습니다.
        <b> 보존연한</b>과 <b>기본 제목</b>도 여기서 정한 값을 그대로 씁니다.
      </p>
      {/* 저장 줄 한 벌(2026-10-18 · web 레인 W8b) — 저장 · 막는 조건(`!dirty || busy`)은 종전 그대로.
          ★ 이 줄이 저장하는 것은 «이름 · 구분» 둘뿐이다 — 결재함 · 보존연한 · 기본 제목 · 아래 «이 양식을 씁니다» 는
            고르는 즉시 저장되므로 이 줄 밖에 그대로 둔다(한 줄은 한 저장 시점).
          ★ 되돌리기(새 동작 · 프론트만) = 고른 양식을 처음 열 때와 같은 값으로(위 select 와 같은 식). */}
      <SaveBar
        note={<>코드는 바꿀 수 없습니다 — <b>이미 그 종류로 올라간 문서</b>가 이 값을 들고 있습니다.</>}
        changedCount={(basic.name.trim() !== docType.name ? 1 : 0)
          + (basic.category !== ((docType.category as DocTypeCategory) ?? 'GENERAL') ? 1 : 0)}
        canSave={dirty && !busy}
        onSave={() => void onPatch(
          docType.id,
          { name: basic.name.trim(), category: basic.category },
          `${basic.name.trim()} 으로 저장했습니다.`,
        )}
        onRevert={() => setBasic({ name: docType.name, category: (docType.category as DocTypeCategory) ?? 'GENERAL' })}
        canRevert={dirty && !busy}
        enableHint={dirty ? null : undefined}
      />

      <Checkbox
        className="hra__check dtp__use-toggle"
        label="이 양식을 씁니다"
        checked={docType.isActive} disabled={busy}
        aria-label={`${docType.name} 사용`}
        onChange={(e) => void onPatch(
          docType.id, { isActive: e.target.checked },
          e.target.checked ? `${docType.name}을(를) 다시 씁니다.` : `${docType.name}을(를) 목록에서 뺐습니다.`,
        )}
      />
      <p className="hra__note">
        <b>지우는 길은 없습니다.</b> 쓰지 않게 된 양식은 사용을 꺼 주세요 — 지우면 이미 그 종류로
        올라간 문서가 무엇인지 알 수 없게 됩니다. 꺼도 이미 올라간 문서는 그대로 돕니다.
      </p>
    </div>
  );
}

/* ═══════════════ 결재선 ═══════════════ */

function LineTab({ docType, busy, onPatch }: {
  docType: DocTypeDto;
  busy: boolean;
  onPatch: (id: string, patch: Parameters<typeof updateDocType>[1], note: string) => Promise<void>;
}) {
  return (
    <div className="dtp__form">
      <div className="dtp__row">
        <FormField className="hra__field" label="기본 단계">
          <Input
            className="hra__num"
            type="number" min={1} max={10}
            aria-label={`${docType.name} 기본 단계 수`}
            value={docType.defaultSteps} disabled={busy}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (v >= 1 && v <= 10 && v !== docType.defaultSteps) {
                void onPatch(docType.id, { defaultSteps: v }, `${docType.name}을(를) ${v}단계로 바꿨습니다.`);
              }
            }}
          />
        </FormField>
        <Checkbox
          className="hra__check"
          label="기안자가 결재선을 고칠 수 있습니다"
          aria-label={`${docType.name} 기안자 수정 허용`}
          checked={docType.allowDrafterEdit} disabled={busy}
          onChange={(e) => void onPatch(
            docType.id, { allowDrafterEdit: e.target.checked },
            e.target.checked
              ? `${docType.name}은(는) 기안자가 결재선을 고칠 수 있습니다.`
              : `${docType.name}은(는) 회사가 정한 결재선으로만 올라갑니다.`,
          )}
        />
      </div>
      <p className="hra__note">
        여기서 정한 단계 수가 <b>기안 화면의 기본 결재선</b>이 됩니다.
        조직 사다리(팀장 → 부서장 → 대표)가 더 길면 <b>사다리를 따릅니다</b> — 단계 수는 기본값이지 상한이 아닙니다.
        사다리를 세우는 자리는 <b>통합설정 › 조직 › 조직도</b>(팀장)와 <b>인사 › 조직 · 권한 › 대표 · 부서장</b>입니다.
      </p>
    </div>
  );
}

/* ═══════════════ 전결 ═══════════════ */

function FinalizeTab({ docType, busy, onPatch }: {
  docType: DocTypeDto;
  busy: boolean;
  onPatch: (id: string, patch: Parameters<typeof updateDocType>[1], note: string) => Promise<void>;
}) {
  const kind = docType.finalizeKind ?? 'NONE';
  const unit = FINALIZE_OPTIONS.find((o) => o.value === kind)?.unit ?? '';
  /** 서식에서 「전결 기준으로 쓴다」로 표시한 칸 — 크기를 이 칸에서 읽는다. */
  const measure = docType.formSchema?.fields.find((f) => f.measure);

  return (
    <div className="dtp__form">
      <div className="dtp__row">
        <FormField className="hra__field" label="무엇으로 재나">
          <Select
            ariaLabel={`${docType.name} 전결 기준 종류`}
            value={kind} disabled={busy}
            options={FINALIZE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            onChange={(picked) => {
              const next = picked as FinalizeKind;
              void onPatch(
                docType.id,
                // 끌 때는 기준값도 함께 비운다 — 남겨 두면 다시 켤 때 옛 값이 살아난다.
                next === 'NONE'
                  ? { finalizeKind: next, finalizeThreshold: null }
                  : { finalizeKind: next, finalizeThreshold: docType.finalizeThreshold ?? (next === 'DAYS' ? 1 : 500000) },
                next === 'NONE'
                  ? `${docType.name}은(는) 전결을 쓰지 않습니다.`
                  : `${docType.name}의 전결 기준을 ${next === 'DAYS' ? '일수' : '금액'}로 바꿨습니다.`,
              );
            }}
          />
        </FormField>

        {kind !== 'NONE' && (
          <>
            <FormField className="hra__field" label="이 값 이하면 전결">
              <Input
                type="number" className="hra__num" min={0}
                step={kind === 'DAYS' ? 0.25 : 10000}
                aria-label={`${docType.name} 전결 기준값`}
                value={docType.finalizeThreshold ?? 0} disabled={busy}
                style={{ width: '7.5rem' }}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (Number.isFinite(v) && v >= 0 && v !== docType.finalizeThreshold) {
                    void onPatch(docType.id, { finalizeThreshold: v }, `${docType.name}의 전결 기준을 ${num(v)}${unit} 이하로 바꿨습니다.`);
                  }
                }}
              />
            </FormField>
            <FormField className="hra__field" label="남길 단계">
              <Input
                type="number" className="hra__num" min={1} max={10}
                aria-label={`${docType.name} 전결 남길 단계`}
                value={docType.finalizeSteps ?? 1} disabled={busy}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (v >= 1 && v <= 10 && v !== (docType.finalizeSteps ?? 1)) {
                    void onPatch(docType.id, { finalizeSteps: v }, `${docType.name}의 전결을 ${v}단계까지로 바꿨습니다.`);
                  }
                }}
              />
            </FormField>
          </>
        )}
      </div>

      {kind !== 'NONE' && (
        <p className="hra__note">
          기준 <b>{num(docType.finalizeThreshold ?? 0)}{unit} 이하</b>인 문서는 사다리에서 만들어진 줄을
          앞에서부터 <b>{docType.finalizeSteps ?? 1}단계만 남기고</b> 윗단계를 뺍니다 —
          반차 하나에 대표까지 갈 필요가 없습니다. 전결은 <b>기안 시점에만</b> 판정하므로
          기준을 바꿔도 이미 올라간 문서는 그대로입니다.
        </p>
      )}

      {/* ★★ 크기를 어디서 읽는지 — 서식의 «전결 기준» 칸이 그 답이다. */}
      <p className={`hra__note${kind !== 'NONE' && !measure ? ' dtp__warn' : ''}`}>
        {kind === 'NONE' ? (
          <>전결을 쓰지 않으면 모든 문서가 <b>사다리 끝까지</b> 올라갑니다.</>
        ) : measure ? (
          <>크기는 서식의 <b>「{measure.label}」</b> 칸에서 읽습니다 — 「칸」 탭에서 <b>전결 기준</b>으로 표시한 칸입니다.</>
        ) : (
          <>
            <b>크기를 읽을 칸이 없습니다.</b> 「칸」 탭에서 숫자 칸 하나를 <b>전결 기준</b>으로
            표시하세요 — 표시가 없으면 기안 화면이 그 값을 따로 받고, <b>크기를 모르면 전결하지 않습니다</b>
            (빈 칸을 0 으로 보면 큰 지출이 팀장 선에서 끝납니다).
          </>
        )}
      </p>
    </div>
  );
}

/* ═══════════════ 미리 보기 ═══════════════ */

/**
 * 기안자가 볼 화면 — **같은 컴포넌트**(`ApprovalFormFields`)로 그린다.
 *
 * ★ 따로 그리면 어느 날 둘이 갈린다. 여기서 적은 값은 아무 데도 안 간다(그 사실을 적는다).
 */
function PreviewModal({ docType, onClose }: { docType: DocTypeDto; onClose: () => void }) {
  const [values, setValues] = useState<FormValues>({});
  const fields = (docType.formSchema?.fields ?? []) as unknown as ApprovalFormField[];

  return (
    <Modal title={`${docType.name} — 미리 보기`} size="lg" onClose={onClose} footer={
      <>
        <ModalFootNote>기안자가 보는 모습입니다 — 여기서 적은 값은 <b>저장되지 않습니다</b>.</ModalFootNote>
        <ModalActions>
          <Button variant="secondary" onClick={onClose}>닫기</Button>
        </ModalActions>
      </>
    }>
      <div className="dtp__preview">
        {/*
          ⚠ 여기 「제목」은 **기안 화면의 문서 제목 칸을 흉내 낸 것**이다 —
            그 규격(정본 B15 「문서 제목 칸」)이 아직 승인 전이라 손짜기로 둔다.
            정본에 서면 기안 화면과 **함께** 갈아탄다(둘이 달라지면 미리 보기가 거짓말이 된다).
        */}
        <label className="hra__field">
          <span>제목</span>
          <input type="text" placeholder={`${docType.name} 제목`} readOnly />
        </label>
        {fields.length === 0 ? (
          <p className="hra__note">
            칸이 없어 <b>제목과 본문만</b> 받습니다 — 「칸」 탭에서 서식을 갖추면 여기 함께 뜹니다.
          </p>
        ) : (
          <ApprovalFormFields fields={fields} value={values} onChange={setValues} />
        )}
      </div>
    </Modal>
  );
}
